import { api } from "services/axios/api";
import { getErrorsByApi } from "shared/helpers/getErrorsByApi";
import { IVidro, IVidroCor, ISelectOption } from "../types/vidro.types";
import { RetornoMidia, RetornoNomeMidia, RetornoRemocaoMidia } from "../../types/midia.types";

// FASE 05 — 4ª validação/Frente 9 2026-07-30: o backend não persiste IdArredondamento
// (coluna ignorada no mapping) — só a string Arredondamento. Ao carregar, derivamos o id
// da string para o select exibir o valor gravado; texto legado/desconhecido fica sem
// seleção (0) e o usuário escolhe entre P/CIMA, SIM e NÃO ao salvar.
const derivarIdArredondamento = (arredondamento?: string): number => {
  switch ((arredondamento || "").trim().toUpperCase()) {
    case "P/CIMA":
      return 1;
    case "SIM":
      return 2;
    case "NÃO":
    case "NAO":
      return 3;
    default:
      return 0;
  }
};

// Plano 15/10/2026 (Frente 6): o ObterPorId devolve as cores com os nomes do VidroDTO
// (fatorSolar, absorcaoEnergetica, ...), mas a aba Cores lê fs/ae/tt/tl/bc/refInt/refExt —
// os 7 campos apareciam vazios em todo vidro com cor. O GET .../Cores já usa os nomes curtos;
// aceitar os dois formatos.
const mapearCorVidro = (c: any): IVidroCor => ({
  id: c.id ?? c.idCorVidro,
  ordem: c.ordem ?? 0,
  idCor: c.idCor,
  corDescricao: c.corDescricao ?? c.cor ?? "",
  fs: c.fs ?? c.fatorSolar ?? 0,
  ae: c.ae ?? c.absorcaoEnergetica ?? 0,
  tt: c.tt ?? c.transmissaoTermica ?? 0,
  tl: c.tl ?? c.transmissaoLuminosidade ?? 0,
  bc: c.bc ?? c.bloqueioCalor ?? 0,
  refInt: c.refInt ?? c.reflexaoInterior ?? 0,
  refExt: c.refExt ?? c.reflexaoExterior ?? 0,
  ativo: c.ativo ?? true,
  idVidro: c.idVidro,
});

// ===== CRUD de Vidro =====

export const obterVidroPorId = async (idVidro: number): Promise<IVidro | null> => {
  try {
    const { data } = await api.get(`/VidroN1/ObterPorId/${idVidro}`);
    
    if (!data) return null;
    
    return {
      idVidro: data.idVidro,
      codigo: data.codigo || "",
      codigoInterno: data.codigoInterno,
      descricao: data.descricao || "",
      idSituacao: data.idSituacao || 1,
      idStatus: data.idStatus || 1,
      idReferencia: data.idReferencia,
      idLinha: data.idLinha,
      idTipoVidro: data.idTipoVidro || 0,
      tipoVidroDescricao: data.tipoVidroDescricao,
      espessura: data.espessura || 0,
      peso: data.peso || 0,
      fatorLH: data.fatorLh || data.fatorLH || 0,
      controleSolar: data.controleSolar ?? false,
      bloqueioRuido: data.bloqueioRuido ?? false,
      idArredondamento: data.idArredondamento || derivarIdArredondamento(data.arredondamento),
      arredondamentoDescricao: data.arredondamento,
      // Mapear campos de mídia
      imagemUrl: data.imagem || undefined,
      imagemNomeArquivo: data.imagemNomeArquivo || undefined,
      // Mesma ordem do GET .../Cores (por Ordem) — o salvar regrava nessa sequência.
      cores: (data.cores || []).map(mapearCorVidro).sort((x: IVidroCor, y: IVidroCor) => x.ordem - y.ordem),
      // Campos de auditoria
      dataUltimaAtualizacao: data.dataAlteracao,
      dataCriacao: data.dataCriacao,
      editavel: data.editavel,
      excluido: data.excluido,
    };
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao obter vidro");
    return null;
  }
};

export const salvarVidro = async (vidro: Partial<IVidro>): Promise<{ id: number; sucesso: boolean; mensagem: string } | null> => {
  try {
    const isEdicao = !!vidro.idVidro;
    const endpoint = isEdicao ? `/VidroN1/Atualizar/${vidro.idVidro}` : '/VidroN1/Criar';
    const method = isEdicao ? 'put' : 'post';
    
    // Limpar valores inválidos antes de enviar
    const dadosLimpos: any = { ...vidro };
    
    // Remover campos de mídia (URLs) do payload - apenas nomes de arquivo são enviados
    delete dadosLimpos.imagemUrl;
    delete dadosLimpos.imagemNomeArquivo;
    delete dadosLimpos.arredondamentoDescricao;
    
    // Cores serão salvas separadamente
    delete dadosLimpos.cores;
    
    // Mapear campos para o formato esperado pelo backend
    dadosLimpos.fatorLh = dadosLimpos.fatorLH;
    delete dadosLimpos.fatorLH;
    
    // Mapear idGrupo para idGrupoMaterial (backend espera idGrupoMaterial)
    // Se não houver idGrupoMaterial e não houver idGrupo, remover para evitar erro de validação
    // O backend validará e retornará erro claro se for obrigatório
    if (dadosLimpos.idGrupo && !dadosLimpos.idGrupoMaterial) {
      dadosLimpos.idGrupoMaterial = dadosLimpos.idGrupo;
    }
    delete dadosLimpos.idGrupo;
    
    // Se idGrupoMaterial for 0, undefined ou negativo, remover para evitar erro de validação
    if (!dadosLimpos.idGrupoMaterial || dadosLimpos.idGrupoMaterial < 1) {
      delete dadosLimpos.idGrupoMaterial;
    }
    
    // Mapear idArredondamento para Arredondamento (string)
    // O backend espera Arredondamento (string) - IdArredondamento é ignorado no mapeamento
    if (dadosLimpos.idArredondamento && dadosLimpos.idArredondamento > 0) {
      // FASE 05 — 4ª validação/Frente 9 2026-07-30: opções oficiais do CEM (mesmos ids do
      // endpoint /ReferenciaDados/Arredondamentos) — o texto é o que grava na coluna Arredondamento.
      const arredondamentoMap: Record<number, string> = {
        1: "P/CIMA",
        2: "SIM",
        3: "NÃO",
      };
      dadosLimpos.Arredondamento = arredondamentoMap[dadosLimpos.idArredondamento] || "P/CIMA";
    } else {
      // Se não tiver idArredondamento, não enviar Arredondamento (será string vazia no backend)
      dadosLimpos.Arredondamento = "";
    }
    // Remover IdArredondamento pois não existe no banco (apenas Arredondamento string)
    delete dadosLimpos.IdArredondamento;
    
    const { data } = await api[method](endpoint, dadosLimpos);
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao salvar vidro");
    return null;
  }
};

export const excluirVidro = async (idVidro: number): Promise<boolean> => {
  try {
    await api.delete(`/VidroN1/Excluir/${idVidro}`);
    return true;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao excluir vidro");
    return false;
  }
};

export const duplicarVidro = async (idVidro: number): Promise<{ id: number; sucesso: boolean; mensagem: string } | null> => {
  try {
    const { data } = await api.post(`/VidroN1/Duplicar/${idVidro}`);
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao duplicar vidro");
    return null;
  }
};

export const validarCodigoUnico = async (codigo: string, idExcluir?: number): Promise<boolean> => {
  try {
    const params: any = { codigo };
    if (idExcluir) params.idExcluir = idExcluir;
    
    const { data } = await api.get('/VidroN1/ValidarCodigoUnico', { params });
    return data.disponivel;
  } catch (error: any) {
    console.error("Erro ao validar código:", error);
    return false;
  }
};

// ===== Listas de Referência =====

export const listarReferencias = async (idTipoTela?: number): Promise<ISelectOption[]> => {
  try {
    const params: any = {};
    if (idTipoTela) params.idTipoTela = idTipoTela;
    const { data } = await api.get('/ReferenciaDados/Referencias', { params });
    return (data || []).map((item: any) => ({ 
      id: item.idReferencia || item.id, 
      nome: item.descricao || item.nome 
    }));
  } catch (error: any) {
    console.error("Erro ao listar referências:", error);
    return [];
  }
};

export const listarLinhas = async (idReferencia?: number): Promise<ISelectOption[]> => {
  try {
    // FASE 05 — S3/PO 2026-05-13: quando idReferencia informado, filtra via tabela ReferenciaLinhas.
    const params: any = {};
    if (idReferencia) params.idReferencia = idReferencia;
    const { data } = await api.get('/ReferenciaDados/Linhas', { params });
    return (data || []).map((item: any) => ({
      id: item.idLinha || item.id,
      nome: item.descricao || item.nome
    }));
  } catch (error: any) {
    console.error("Erro ao listar linhas:", error);
    return [];
  }
};

export const listarTiposVidro = async (): Promise<ISelectOption[]> => {
  try {
    const { data } = await api.get('/ReferenciaDados/TiposVidro');
    return (data || []).map((item: any) => ({ 
      id: item.idTipoVidro || item.id, 
      nome: item.descricao || item.nome 
    }));
  } catch (error: any) {
    console.error("Erro ao listar tipos de vidro:", error);
    return [];
  }
};

export const listarCoresVidro = async (): Promise<ISelectOption[]> => {
  try {
    const { data } = await api.get('/ReferenciaDados/CoresVidro');
    return (data || []).map((item: any) => ({ 
      id: item.id || item.idCorMaterial || item.IdCorMaterial, 
      nome: item.nome || item.descricao || item.Nome || item.Descricao,
      descricao: item.descricao || item.Descricao || item.nome || item.Nome,
    }));
  } catch (error: any) {
    console.warn("Erro ao listar cores para vidros:", error);
    return [];
  }
};

export const listarArredondamentos = async (): Promise<ISelectOption[]> => {
  try {
    const { data } = await api.get('/ReferenciaDados/Arredondamentos');
    return (data || []).map((item: any) => ({ 
      id: item.id || 0, 
      nome: item.nome || item.descricao || item.nome || "",
      descricao: item.descricao || item.nome 
    }));
  } catch (error: any) {
    console.error("Erro ao listar arredondamentos:", error);
    return [];
  }
};

// ===== Gerenciamento de Mídias =====

// Plano 21/09/2026 (Frente 3): as escritas de mídia devolvem também o `id` do registro gravado —
// com o produto Disponível, a API grava na cópia de trabalho e a tela precisa seguir para ela.
export const uploadImagem = async (idVidro: number, file: File, nomeArquivo?: string): Promise<RetornoMidia | null> => {
  try {
    const formData = new FormData();
    formData.append('arquivo', file);
    // Ajustes 25/08 (plano de mídias, Etapa A): o servidor grava com este nome — sem ele,
    // o arquivo era renomeado para um GUID e perdia o vínculo com os produtos.
    if (nomeArquivo) formData.append('nomeArquivo', nomeArquivo);
    const { data } = await api.post(`/VidroN1/${idVidro}/Midias/UploadImagem`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data?.url ? { url: data.url, id: data.id ?? idVidro } : null;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao fazer upload da imagem");
    return null;
  }
};

export const removerImagem = async (idVidro: number): Promise<RetornoRemocaoMidia | null> => {
  try {
    const { data } = await api.delete(`/VidroN1/${idVidro}/Midias/RemoverImagem`);
    return data?.sucesso ? { sucesso: true, id: data.id ?? idVidro } : null;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao remover imagem");
    return null;
  }
};

// ===== Histórico =====

export const obterHistoricoVidro = async (idVidro: number): Promise<any[] | null> => {
  try {
    const { data } = await api.get(`/VidroN1/${idVidro}/Historico`);
    return data?.items || [];
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao obter histórico de atualizações");
    return null;
  }
};

// ===== Cores do Vidro =====

export const salvarCoresVidro = async (idVidro: number, cores: IVidroCor[]): Promise<boolean> => {
  try {
    await api.post(`/VidroN1/${idVidro}/Cores`, cores);
    return true;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao salvar cores do vidro");
    return false;
  }
};

export const obterCoresVidro = async (idVidro: number): Promise<IVidroCor[] | null> => {
  try {
    const { data } = await api.get(`/VidroN1/${idVidro}/Cores`);
    return data?.items || [];
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao obter cores do vidro");
    return null;
  }
};

// ===== Definir apenas o NOME do arquivo (sem upload) =====

/**
 * Reporte de 31/08 (item 6): grava só o nome informado, sem enviar arquivo — é assim que o
 * produto assume uma mídia que já foi carregada por outro produto da empresa (F05A-P3b).
 * Devolve `arquivoExiste` para a tela avisar quando o nome ainda não tem arquivo no acervo.
 */
export const definirNomeMidiaVidro = async (
  idVidro: number,
  nomeArquivo: string
): Promise<RetornoNomeMidia | null> => {
  try {
    const { data } = await api.put(`/VidroN1/${idVidro}/Midias/DefinirNome`, {
      tipoMidia: "figura",
      nomeArquivo,
    });
    return data ?? null;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao gravar o nome do arquivo");
    return null;
  }
};
